// One open canvas, like the app's BoardRepository + BoardLive: the elements from
// GET /v1/boards/{id}?since=, live over the realtime socket (board.open/close/ops/presence), own
// changes applied at once and sent as bundles (one in flight, re-sent after 5 s, POST /ops without a
// socket), merged by the per-element rule. Undo writes the old state as a new version on top, so it
// syncs like any other change (BoardHistory.kt).

import { useSyncExternalStore } from "react";
import type { BoardResponse } from "../api/contract.gen.ts";
import type { Session } from "../auth/session.ts";
import type { Realtime, ServerFrame } from "../realtime/realtime.ts";
import { bump, type El, merge, sorted } from "./model.ts";

interface BoardPage {
  board: BoardResponse;
  elements: El[];
  seq: number;
  more: boolean;
  full: boolean;
}

interface OpsAnswer {
  seq: number;
  accepted: string[];
  rejected: El[];
}

export interface Presence {
  user: string;
  x?: number;
  y?: number;
  selection?: string[];
  laser?: [number, number][];
  at: number;
}

const RESEND_MS = 5_000;
const BUNDLE_MS = 50;
const PRESENCE_MS = 66;
const HISTORY = 100;

export class BoardSession {
  board: BoardResponse | null = null;
  readonly elements = new Map<string, El>();
  readonly presence = new Map<string, Presence>();
  readonly drafts = new Map<string, Map<string, El>>();
  present: string[] = [];
  error: string | null = null;
  private seq = 0;
  private version = 0;
  private outbox = new Map<string, El>();
  private inFlight: { clientSeq: number; elements: El[]; at: number } | null = null;
  private clientSeq = 0;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private lastPresence = 0;
  private readonly undo: El[][] = [];
  private readonly redo: El[][] = [];
  private readonly listeners = new Set<() => void>();
  private unsubscribe: (() => void) | null = null;
  readonly id: string;
  private readonly session: Session;
  private readonly realtime: Realtime;

  constructor(id: string, session: Session, realtime: Realtime) {
    this.id = id;
    this.session = session;
    this.realtime = realtime;
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  getVersion = () => this.version;

  async open(): Promise<void> {
    this.unsubscribe = this.realtime.subscribe((f) => this.onFrame(f));
    await this.fetch(0);
    this.realtime.send({ t: "board.open", board: this.id });
    this.resendTimer = setInterval(() => {
      if (this.inFlight && Date.now() - this.inFlight.at > RESEND_MS) this.send(true);
      // Old presence goes (75 s without news, as the app).
      for (const [u, p] of this.presence) if (Date.now() - p.at > 75_000) this.presence.delete(u);
    }, 1000);
  }

  close() {
    this.realtime.send({ t: "board.close", board: this.id });
    this.unsubscribe?.();
    if (this.resendTimer) clearInterval(this.resendTimer);
    if (this.flushTimer) clearTimeout(this.flushTimer);
    // What is still waiting goes by HTTP.
    if (this.outbox.size > 0) void this.post([...this.outbox.values()]);
  }

  /** The elements to draw, with others' drafts on top of their stored states. */
  visible(): El[] {
    const all = new Map(this.elements);
    for (const d of this.drafts.values()) for (const [id, e] of d) all.set(id, e);
    return sorted(all.values());
  }

  /**
   * A local change: [changed] are new states (already bumped, or created at version 1). [record]
   * adds an undo step with the states they replace.
   */
  apply(changed: El[], record = true) {
    if (changed.length === 0) return;
    if (record) {
      this.undo.push(changed.map((e) => this.elements.get(e.id) ?? { ...e, isDeleted: true, version: 0 }));
      if (this.undo.length > HISTORY) this.undo.shift();
      this.redo.length = 0;
    }
    for (const e of changed) {
      this.elements.set(e.id, e);
      this.outbox.set(e.id, e);
    }
    this.changed();
    this.scheduleFlush();
  }

  /** Live movement of others' eyes (board.ops with draft): not stored, not in the outbox. */
  draft(elements: El[]) {
    this.realtime.send({ t: "board.ops", board: this.id, client_seq: 0, elements: elements.slice(0, 20), draft: true });
  }

  canUndo() {
    return this.undo.length > 0;
  }

  canRedo() {
    return this.redo.length > 0;
  }

  undoLast() {
    this.step(this.undo, this.redo);
  }

  redoLast() {
    this.step(this.redo, this.undo);
  }

  presenceAt(x: number, y: number, selection: string[], laser?: [number, number][]) {
    const now = Date.now();
    if (now - this.lastPresence < PRESENCE_MS) return;
    this.lastPresence = now;
    this.realtime.send({ t: "board.presence", board: this.id, x, y, selection: selection.slice(0, 200), ...(laser ? { laser: laser.slice(-64) } : {}) });
  }

  private step(from: El[][], to: El[][]) {
    const states = from.pop();
    if (!states) return;
    to.push(states.map((s) => this.elements.get(s.id) ?? { ...s, isDeleted: true }));
    // The old content as a new version on top of whatever is there now.
    const next = states.map((old) => {
      const current = this.elements.get(old.id);
      const base = old.version === 0 ? { ...old, isDeleted: true } : old;
      return bump({ ...base, version: current?.version ?? base.version });
    });
    this.apply(next, false);
  }

  private scheduleFlush() {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.send(false);
    }, BUNDLE_MS);
  }

  /** One bundle in flight at a time; [again]: the same bundle once more (no ack in 5 s). */
  private send(again: boolean) {
    if (this.inFlight && !again) return;
    if (!this.inFlight) {
      if (this.outbox.size === 0) return;
      this.inFlight = { clientSeq: ++this.clientSeq, elements: [...this.outbox.values()], at: Date.now() };
    }
    this.inFlight.at = Date.now();
    if (this.realtime.isConnected) {
      this.realtime.send({ t: "board.ops", board: this.id, client_seq: this.inFlight.clientSeq, elements: this.inFlight.elements });
    } else {
      const bundle = this.inFlight;
      void this.post(bundle.elements).then((a) => a && this.confirmed(bundle.clientSeq, a.seq, a.rejected));
    }
  }

  private async post(elements: El[]): Promise<OpsAnswer | null> {
    try {
      return await this.session.call<OpsAnswer>(`/v1/boards/${this.id}/ops`, { method: "POST", json: { elements } });
    } catch (e) {
      this.refused(String(e));
      return null;
    }
  }

  private confirmed(clientSeq: number, seq: number, rejected: El[]) {
    const bundle = this.inFlight;
    if (!bundle || bundle.clientSeq !== clientSeq) return;
    this.inFlight = null;
    // Sent states leave the outbox unless a newer local state replaced them meanwhile.
    for (const e of bundle.elements) if (this.outbox.get(e.id) === e) this.outbox.delete(e.id);
    merge(this.elements, rejected);
    if (seq === this.seq + 1) this.seq = seq;
    else void this.fetch(this.seq);
    this.changed();
    this.send(false);
  }

  /** The server refused a bundle (400/409): drop it and load the canvas again. */
  private refused(message: string) {
    const bundle = this.inFlight;
    this.inFlight = null;
    if (bundle) for (const e of bundle.elements) this.outbox.delete(e.id);
    this.error = message.includes("BOARD_FULL") ? "Das Canvas ist voll (5000 Elemente)." : message.includes("BOARD_IMAGES_FULL") ? "Höchstens 30 Bilder pro Canvas." : "Eine Änderung wurde nicht übernommen.";
    void this.fetch(0, true);
  }

  private async fetch(since: number, replace = false): Promise<void> {
    let after = since;
    for (;;) {
      const page = await this.session.call<BoardPage>(`/v1/boards/${this.id}?since=${after}`);
      this.board = page.board;
      this.present = page.board.present ?? [];
      if (page.full || replace) {
        this.elements.clear();
        replace = false;
      }
      merge(this.elements, page.elements);
      // Own states still waiting stay on top.
      for (const e of this.outbox.values()) this.elements.set(e.id, e);
      this.seq = page.seq;
      after = page.seq;
      if (!page.more) break;
    }
    this.changed();
  }

  private onFrame(f: ServerFrame) {
    if (f.board !== this.id) return;
    switch (f.t) {
      case "board.ops": {
        const by = f.by as string;
        const elements = (f.elements as El[]) ?? [];
        if (f.draft) {
          const d = this.drafts.get(by) ?? new Map<string, El>();
          for (const e of elements) d.set(e.id, e);
          this.drafts.set(by, d);
          setTimeout(() => this.drafts.get(by) === d && (this.drafts.delete(by), this.changed()), 2000);
        } else {
          this.drafts.delete(by);
          merge(this.elements, elements);
          for (const e of this.outbox.values()) if (this.elements.get(e.id) !== e) this.outbox.delete(e.id);
          const seq = f.seq as number | undefined;
          if (seq !== undefined) {
            if (seq === this.seq + 1) this.seq = seq;
            else if (seq > this.seq) void this.fetch(this.seq);
          }
        }
        this.changed();
        break;
      }
      case "board.ack":
        this.confirmed(f.client_seq as number, f.seq as number, (f.rejected as El[]) ?? []);
        break;
      case "board.error":
        if (this.inFlight?.clientSeq === f.client_seq) this.refused(String(f.code));
        break;
      case "board.hint":
        void this.fetch(this.seq);
        break;
      case "board.presence": {
        const user = f.user as string;
        this.presence.set(user, { user, x: f.x as number, y: f.y as number, selection: f.selection as string[], laser: f.laser as [number, number][] | undefined, at: Date.now() });
        this.changed();
        break;
      }
      case "board.present":
        this.present = (f.users as string[]) ?? [];
        this.changed();
        break;
    }
  }

  private changed() {
    this.version++;
    for (const l of this.listeners) l();
  }
}

export function useBoard(session: BoardSession | null): number {
  return useSyncExternalStore(session?.subscribe ?? (() => () => undefined), session?.getVersion ?? (() => 0));
}
